import { ModalAction, useAutoGrowTextarea } from '@overlayqa/shared/ui';
/**
 * ChatThread — the AI help-agent conversation panel rendered inside the
 * dashboard FAB popover (replaces the one-shot feedback form).
 *
 * Behaviors: client-only assistant greeting (never persisted), Enter sends /
 * Shift+Enter newline, sends are serialized (input disabled while a reply is
 * pending), and distinct copy for the 409 turn-cap and 429 rate-limit vs
 * generic errors.
 * Analytics: `help_agent_message_sent` is captured SERVER-side by the route —
 * the client deliberately does not fire it (double-count).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { HELP_CHAT_COPY } from '@overlayqa/shared';
import { useAuth } from '../lib/auth';
import { helpAgentApi } from '../lib/api';
import { log } from '@/lib/logger';
import { HELP_GREETING as GREETING } from './help/greeting';

// Shared with the extension thread and the server's 409/429 bodies so the
// three cannot drift; see packages/shared/src/constants/helpChatCopy.ts.
const COPY = HELP_CHAT_COPY;

interface ThreadMessage {
  role: 'user' | 'assistant';
  content: string;
  /** Stable identity so a failed turn can be found again after later appends. */
  id: number;
  /**
   * Set when the send never reached the server. The turn stays visible (no
   * typed text is ever lost) but is labelled undelivered, because the server's
   * conversation does not contain it: a later successful send appends to that
   * same conversation, so the assistant answers from a history missing this
   * message. Showing it as a normal turn would hide that from the user.
   */
  undelivered?: boolean;
}

let nextMessageId = 1;

/**
 * The conversation outlives the panel. FeedbackFab unmounts ChatThread when it
 * closes, and an outside click closes it, so per-mount state meant one stray
 * click destroyed the transcript, the draft, and the conversation id. Reopening
 * then started a NEW server conversation and the assistant lost every prior
 * turn. Module scope keeps it for the page session (same approach as the nudge
 * latches); a reload still starts fresh, and "Start a new chat" clears it.
 */
interface HelpSession {
  messages: ThreadMessage[];
  input: string;
  conversationId?: string;
}
let session: HelpSession | null = null;

const freshMessages = (): ThreadMessage[] => [
  { role: 'assistant', content: GREETING, id: nextMessageId++ },
];

/** Test-only: drop the persisted page-session conversation. */
export function resetHelpConversationForTests(): void {
  session = null;
  nextMessageId = 1;
}

export function ChatThread() {
  const { getToken } = useAuth();
  const [messages, setMessages] = useState<ThreadMessage[]>(
    () => session?.messages ?? freshMessages(),
  );
  const [input, setInput] = useState(() => session?.input ?? '');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const conversationIdRef = useRef<string | undefined>(session?.conversationId);
  const pendingRef = useRef(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Grows with the draft up to four lines, then scrolls (help-chat-run-workflow decision 7).
  const inputRef = useRef<HTMLTextAreaElement>(null);
  useAutoGrowTextarea(inputRef, input);

  // conversationIdRef is assigned synchronously before the setMessages that
  // follows it, so this effect always writes the current id alongside them.
  useEffect(() => {
    session = { messages, input, conversationId: conversationIdRef.current };
  }, [messages, input]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, pending]);

  /**
   * Delivers one user turn. `existingId` re-sends an already-rendered
   * undelivered turn (Retry); otherwise the text is appended as a new turn.
   * The composer is never written to here, so a draft typed while a send is in
   * flight survives the send failing.
   */
  const deliver = useCallback(
    async (text: string, existingId?: number) => {
      if (pendingRef.current) return;
      log.info('[ChatThread] sending message', { length: text.length, retry: existingId != null });

      const id = existingId ?? nextMessageId++;
      pendingRef.current = true;
      setPending(true);
      setError(null);
      setMessages((prev) =>
        existingId != null
          ? prev.map((m) => (m.id === existingId ? { ...m, undelivered: false } : m))
          : [...prev, { role: 'user', content: text, id }],
      );

      try {
        const token = await getToken();
        if (!token) throw new Error('Not authenticated');
        const res = await helpAgentApi.chat(token, {
          ...(conversationIdRef.current ? { conversationId: conversationIdRef.current } : {}),
          message: text,
        });
        conversationIdRef.current = res.conversationId;
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: res.reply, id: nextMessageId++ },
        ]);
      } catch (err) {
        log.error('[ChatThread] send failed', {
          error: err instanceof Error ? err.message : String(err),
        });
        setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, undelivered: true } : m)));
        // Structural check instead of `instanceof ApiError`: importing lib/api/client
        // here would pull its module-scope import.meta.env read into ts-jest.
        const status = (err as { status?: unknown }).status;
        const code = (err as { code?: unknown }).code;
        if (code === 'CONVERSATION_FULL' || status === 409) {
          setError(COPY.full);
        } else if (status === 429) {
          setError(COPY.rate);
        } else {
          setError(COPY.generic);
        }
      } finally {
        pendingRef.current = false;
        setPending(false);
      }
    },
    [getToken],
  );

  const handleSend = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed || pendingRef.current) return;
    setInput('');
    void deliver(trimmed);
  }, [input, deliver]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void handleSend();
      }
    },
    [handleSend],
  );

  // Turn-cap escape hatch: the 409 copy promises "start a new one", and this
  // is that affordance — a fresh client thread; the next send creates a new
  // server conversation because conversationIdRef is cleared.
  const handleNewChat = useCallback(() => {
    log.info('[ChatThread] starting a new chat after turn cap');
    conversationIdRef.current = undefined;
    session = null; // so the reset survives a close/reopen too
    setMessages(freshMessages());
    setInput('');
    setError(null);
  }, []);

  return (
    <div className="flex h-[380px] min-h-0 flex-col">
      <div ref={scrollRef} className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1" aria-live="polite">
        {messages.map((m) => (
          <div
            key={m.id}
            className={m.role === 'user' ? 'flex flex-col items-end' : 'flex flex-col items-start'}
          >
            <div
              className={
                m.role === 'user'
                  ? `max-w-[85%] rounded-xl px-3 py-2 oq-text-body text-white ${
                      m.undelivered
                        ? 'border border-solid border-red-300 bg-primary-500/50'
                        : 'bg-primary-500'
                    }`
                  : 'max-w-[85%] rounded-xl bg-gray-100 px-3 py-2 oq-text-body text-gray-900'
              }
            >
              <p className="whitespace-pre-wrap">{m.content}</p>
            </div>
            {m.undelivered && (
              <p className="mt-1 flex items-center gap-2 text-xs text-red-600" role="alert">
                <span>Not sent</span>
                <button
                  type="button"
                  onClick={() => void deliver(m.content, m.id)}
                  disabled={pending}
                  className="font-medium underline hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                ><ModalAction kind="retry">
                  Retry
                </ModalAction></button>
              </p>
            )}
          </div>
        ))}
        {pending && (
          <div className="flex justify-start" aria-label="Assistant is typing">
            <div className="rounded-xl bg-gray-100 px-3 py-2">
              <span className="inline-flex gap-1">
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:120ms]" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:240ms]" />
              </span>
            </div>
          </div>
        )}
        {error && (
          <div className="space-y-1">
            <p className="text-xs text-red-600" role="alert">{error}</p>
            {error === COPY.full && (
              <button
                type="button"
                onClick={handleNewChat}
                className="text-xs font-medium text-primary-600 underline hover:text-primary-700"
              ><ModalAction kind="add">
                Start a new chat
              </ModalAction></button>
            )}
          </div>
        )}
      </div>

      <div className="mt-3 border-t border-gray-100 pt-3">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            className="min-h-[var(--oq-control-app-form-height)] w-full resize-none rounded-lg border border-gray-300 px-3 py-2 oq-text-body text-gray-900 outline-none focus:border-primary-500 focus:ring-[3px] focus:ring-primary-500/20"
            placeholder="Ask a question…"
            aria-label="Message"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            maxLength={2000}
            rows={1}
            autoFocus
          />
          <button
            type="button"
            onClick={() => void handleSend()}
            disabled={!input.trim() || pending}
            className="btn-primary"
          ><ModalAction kind="send">
            Send
          </ModalAction></button>
        </div>
      </div>
    </div>
  );
}
